import type { NotifyModelProps } from '@mail/3promo';
import type { ReactRendererComponent } from '@mail/3promo/render/react';
import { xray } from 'lib/xray';
import React, { useCallback, useEffect, useState } from 'react';
import type { FakeDoorTemplateData } from 'reactApp/modules/promoNew/types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store } from 'reactApp/store';

import { Content } from './Content';

const sendMetrics = (key: keyof typeof METRICS_MAP) => {
    const params = METRICS_MAP[key];
    const state = store.getState();
    const isPaidUser = UserSelectors.isPaidUser(state);
    const { usedSize, freeSize } = UserQuotaSelectors.getSpace(state);

    if (params) {
        xray.send(params.t, {
            p: 'fakedoor',
            dwh: {
                paid_user: isPaidUser,
                free_quota: freeSize,
                size_quota: usedSize,
                project: 'cloud',
                name: 'cloud_promo_balloon',
                type_element: params.typeEl,
                name_element: params.nameEl,
            },
        });
    }
};

type PromoShowIntervals = '10s' | '30s' | '1m' | 'above_1m';
const getPromoShowInterval = (n: number): PromoShowIntervals => {
    if (n <= 10) {
        return '10s';
    }

    if (n <= 30) {
        return '30s';
    }

    if (n <= 60) {
        return '1m';
    }

    return 'above_1m';
};

const STUB_CONTENT = {
    title: 'Спасибо за интерес',
    text: 'Функции пока нет, но благодаря вам теперь мы знаем, что она нужна',
    buttons: {
        complete: 'Хорошо',
    },
    image: {
        light: 'https://imgs2.imgsmail.ru/static/cloud/promo/img-promo-progress-light.png',
        dark: 'https://imgs2.imgsmail.ru/static/cloud/promo/img-promo-progress-dark.png',
    },
};

const METRICS_MAP = {
    promoShow: { t: 'show', typeEl: 'modal', nameEl: 'modal_offer' },
    promoHover: { t: 'hover', typeEl: 'modal', nameEl: 'modal_offer' },
    promoButtonHover: { t: 'hover', typeEl: 'button', nameEl: 'button_offer' },
    promoButtonClick: { t: 'click', typeEl: 'button', nameEl: 'button_offer' },
    promoCancelHover: { t: 'hover', typeEl: 'cross', nameEl: 'cross_offer' },
    promoCancelClick: { t: 'click', typeEl: 'cross', nameEl: 'cross_offer' },
    stubShow: { t: 'show', typeEl: 'modal', nameEl: 'modal_thanks' },
    stubHover: null,
    stubButtonHover: { t: 'hover', typeEl: 'button', nameEl: 'button_ok' },
    stubButtonClick: { t: 'click', typeEl: 'button', nameEl: 'button_ok' },
    stubCancelHover: null,
    stubCancelClick: { t: 'click', typeEl: 'cross', nameEl: 'cross_ok' },
};

export const UniversalFakeDoorPromo: ReactRendererComponent<NotifyModelProps<FakeDoorTemplateData>> = ({
    close,
    complete,
    hide,
    templateData,
    closable,
}) => {
    const [stage, setStage] = useState<'promo' | 'stub'>('promo');

    useEffect(() => {
        const start = new Date();

        return () => {
            xray.send('3promo-fd-unmount');

            const end = new Date();
            const seconds = Math.floor((end.valueOf() - start.valueOf()) / 1000);
            const interval = getPromoShowInterval(seconds);

            xray.send(`3promo-fd-show-period-${interval}`);
        };
    }, []);

    useEffect(() => {
        sendMetrics(`${stage}Show`);
        xray.send(`3promo-fd-show-container-${stage}`);
    }, [stage]);

    const handleHoverPromo = useCallback(() => {
        sendMetrics(`${stage}Hover`);
        xray.send(`3promo-fd-hover-container-${stage}`);
    }, [stage]);

    const handleHoverButton = useCallback(() => {
        sendMetrics(`${stage}ButtonHover`);
        xray.send(`3promo-fd-hover-button-${stage}`);
    }, [stage]);

    const handleClickButton = useCallback(() => {
        if (stage === 'stub') {
            hide();
        } else {
            complete();
        }

        sendMetrics(`${stage}ButtonClick`);
        xray.send(`3promo-fd-click-button-${stage}`);
        setStage('stub');
    }, [complete, hide, stage]);

    const handleHoverCancel = useCallback(() => {
        sendMetrics(`${stage}CancelHover`);
        xray.send(`3promo-fd-hover-cancel-${stage}`);
    }, [stage]);

    const handleClickCancel = useCallback(() => {
        sendMetrics(`${stage}CancelClick`);
        xray.send(`3promo-fd-click-cancel-${stage}`);

        if (stage === 'stub') {
            hide();
        } else {
            close();
        }
    }, [close, hide, stage]);

    return (
        <Content
            closable={closable}
            templateData={stage === 'promo' ? templateData : STUB_CONTENT}
            handleHoverPromo={handleHoverPromo}
            handleHoverButton={handleHoverButton}
            handleClickButton={handleClickButton}
            handleHoverCancel={handleHoverCancel}
            handleClickCancel={handleClickCancel}
        />
    );
};
